import Flag from 'components/Flag/Flag';
import ProductBreadCrumbs from 'components/ProductPage/ProductBreadCrumbs/ProductBreadCrumbs';
import {
    Divider, Flex, Grid, Link, Text
} from 'components/ui';
import { DebouncedResize } from 'constants/events';
import React, {
    useRef, useReducer, useCallback, useEffect
} from 'react';
import { mediaQueries, space } from 'style/config';
import languageLocale from 'utils/LanguageLocale';
import marketingFlagsUtil from 'utils/MarketingFlags';
import skuUtils from 'utils/Sku';
import Location from 'utils/Location';
import urlUtils from 'utils/Url';
import { wrapFunctionalComponent } from 'utils/framework';
import rougeExclusiveUtils from 'utils/rougeExclusive';
import RwdProductPageEN from 'components/ProductPage/locales/RwdProductPage_en_US';
import RwdProductPageFR from 'components/ProductPage/locales/RwdProductPage_fr_CA';

/*
[CRMTS-1395]
   Accordingly with this commit https://gitlab.lipstack.sephoraus.com/illuminate/frontend/ufe/-/commit/fe596327363fa5d344d7fe29b86da8071d412a7c
   GitHub link: https://github.com/Sephora-US-Digital/illuminate-frontend-ufe/commit/fe596327363fa5d344d7fe29b86da8071d412a7c
   HIDE_AND_PLACEMENT was decommissioned but not fully removed from code by any developer, so I'm documenting this change here.
   import { HIDE_AND_PLACEMENT } from 'constants/TestTarget';
*/
import typography from 'style/typography';

const { getLink } = urlUtils;
const getText = text => languageLocale.getLocalisedDataFromResource(RwdProductPageEN, RwdProductPageFR)(text);

const INFO_INITIAL = {
    hasOverflow: false,
    showMore: false,
    maxHeight: null,
    isMeasuring: false
};

function infoReducer(state, action) {
    switch (action.type) {
        case 'START_MEASURE':
            return { ...state, isMeasuring: true, showMore: false, maxHeight: null };
        case 'END_MEASURE':
            return {
                ...state,
                isMeasuring: false,
                showMore: action.preservedShowMore,
                maxHeight: action.maxHeight,
                hasOverflow: action.hasOverflow
            };
        case 'TOGGLE_SHOW_MORE':
            return { ...state, showMore: !state.showMore };
        case 'RESET_SHOW_MORE':
            return { ...state, showMore: false };
        default:
            return state;
    }
}

// eslint-disable-next-line complexity
const Info = ({
    currentSku,
    description,
    title,
    dataAt,
    shouldTruncate = true,
    product,
    productBreadcrumbsExperience,
    hideBreadcrumbs,
    isRegularProductSmallView
}) => {
    const [state, dispatch] = useReducer(infoReducer, INFO_INITIAL);
    const textRef = useRef(null);
    const preservedShowMoreRef = useRef(false);
    const showMoreRef = useRef(state.showMore);
    showMoreRef.current = state.showMore;
    const scrollPreserveRef = useRef(null);

    const triggerMeasure = useCallback(() => {
        preservedShowMoreRef.current = showMoreRef.current;
        dispatch({ type: 'START_MEASURE' });
    }, []);

    useEffect(() => {
        if (!state.isMeasuring || !textRef.current) {
            return;
        }

        const desc = textRef.current;
        dispatch({
            type: 'END_MEASURE',
            preservedShowMore: preservedShowMoreRef.current,
            maxHeight: desc.offsetHeight,
            hasOverflow: desc.scrollHeight > desc.offsetHeight
        });
    }, [state.isMeasuring]);

    useEffect(() => {
        triggerMeasure();
        window.addEventListener(DebouncedResize, triggerMeasure);

        return () => window.removeEventListener(DebouncedResize, triggerMeasure);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const isFirstDescRender = useRef(true);
    useEffect(() => {
        if (isFirstDescRender.current) {
            isFirstDescRender.current = false;

            return;
        }

        dispatch({ type: 'RESET_SHOW_MORE' });
    }, [description]);

    useEffect(() => {
        if (scrollPreserveRef.current === null) {
            return;
        }

        window.scroll(0, scrollPreserveRef.current);
        scrollPreserveRef.current = null;

        if (state.showMore && textRef.current) {
            textRef.current.focus();
        }
    }, [state.showMore]);

    const toggleOverflow = () => {
        scrollPreserveRef.current = window.scrollY;
        dispatch({ type: 'TOGGLE_SHOW_MORE' });
    };

    const handleLearnMoreClick = e => {
        e.preventDefault();
        Location.navigateTo(e, getLink('/beauty/final-sale-info'));
    };

    const isHiddenCategory = product?.parentCategory && product?.parentCategory?.categoryId === skuUtils.HIDDEN_CATEGORY_ID;

    let productFlags;

    if (currentSku) {
        productFlags = marketingFlagsUtil.getAboutTheProductFlags(currentSku);
        const { biExclusiveLevel, isReturnable } = currentSku;

        if (biExclusiveLevel !== skuUtils.biExclusiveLevels.NONE) {
            if (
                biExclusiveLevel === skuUtils.biExclusiveLevels.ROUGE ||
                biExclusiveLevel === skuUtils.biExclusiveLevels.VIB ||
                biExclusiveLevel === skuUtils.biExclusiveLevels.BI
            ) {
                productFlags.push({
                    name: rougeExclusiveUtils.isRougeExclusiveEnabled ? getText('rougeBadge') : 'Rouge',
                    activeColor: !!rougeExclusiveUtils.isRougeExclusiveEnabled
                });
            }

            if (biExclusiveLevel === skuUtils.biExclusiveLevels.VIB || biExclusiveLevel === skuUtils.biExclusiveLevels.BI) {
                productFlags.push({ name: 'VIB', activeColor: false });
            }

            if (biExclusiveLevel === skuUtils.biExclusiveLevels.BI) {
                productFlags.push({ name: 'Insider', activeColor: false });
            }
        }

        if (isReturnable === false) {
            // this is because item.sku.isReturnable we must check for a false value
            productFlags.push({ name: getText('finalSaleProductFlag'), activeColor: true });
        }
    }

    const { showMore, maxHeight, hasOverflow } = state;
    const learnMoreLink = getLink('/beauty/final-sale-info');

    return (
        <>
            <Divider />
            <Text
                is='h2'
                lineHeight='tight'
                marginTop='1em'
                marginBottom={productBreadcrumbsExperience && [2, 4]}
                // marginBottom={productBreadcrumbsExperience && productBreadcrumbsExperience === HIDE_AND_PLACEMENT.CURRENT && [2, 4]}
                fontSize={['md', 'lg']}
                fontWeight='bold'
                children={title}
                data-at={Sephora.debug.dataAt(dataAt)}
            />
            {productBreadcrumbsExperience &&
            //productBreadcrumbsExperience === HIDE_AND_PLACEMENT.CHANGE_PLACEMENT &&
            !isHiddenCategory &&
            !hideBreadcrumbs ? (
                    <ProductBreadCrumbs
                        isRegularProductSmallView={isRegularProductSmallView}
                        productBreadcrumbsExperience={productBreadcrumbsExperience}
                        parentCategory={product?.parentCategory}
                    />
                ) : (
                    <div
                        css={{
                            height: space[4],
                            [mediaQueries.sm]: {
                                height: space[5]
                            }
                        }}
                    />
                )}
            <Grid
                alignItems='flex-start'
                columns={[1, null, '212px 1fr']}
                marginTop={productBreadcrumbsExperience && [2, 4]}
                // marginTop={productBreadcrumbsExperience && productBreadcrumbsExperience === HIDE_AND_PLACEMENT.CHANGE_PLACEMENT && [2, 4]}
                gap={[5, null, 0]}
                marginBottom={[5, 7]}
            >
                <Grid gap={2}>
                    {currentSku && (
                        <>
                            <Text
                                is='p'
                                fontSize='sm'
                                color='gray'
                                data-at='item_sku'
                            >
                                {getText('item')} {currentSku.skuId}
                            </Text>
                            {productFlags && productFlags.length > 0 && (
                                <Flex
                                    flexWrap='wrap'
                                    marginRight={[null, null, 6]}
                                    marginX='-.2em'
                                    marginTop='-.6em'
                                >
                                    {productFlags.map((flag, key) => (
                                        <Flag
                                            key={flag.name || key}
                                            backgroundColor={flag.activeColor ? 'red' : 'black'}
                                            marginTop='.6em'
                                            marginX='.2em'
                                        >
                                            {flag.name}
                                        </Flag>
                                    ))}
                                </Flex>
                            )}
                        </>
                    )}
                </Grid>
                <div>
                    {currentSku?.isReturnable === false && (
                        // this is because item.sku.isReturnable we must check for a false value
                        <Flex
                            is='p'
                            flexWrap='wrap'
                            lineHeight={[null, 'relaxed']}
                            color='red'
                            columnGap={3}
                            marginBottom={5}
                        >
                            <span>{getText('finalSaleNoReturns')}</span>
                            <Link
                                href={learnMoreLink}
                                onClick={handleLearnMoreClick}
                                cursor={'pointer'}
                                color='blue'
                                padding={1}
                                margin={-1}
                                children={getText('learnMore')}
                            />
                        </Flex>
                    )}
                    <Text
                        ref={textRef}
                        is='div'
                        tabIndex={-1}
                        role='region'
                        aria-label={title}
                        lineHeight={[null, 'relaxed']}
                        maxWidth={824}
                        numberOfLines={shouldTruncate && !showMore && !maxHeight ? 7 : null}
                        maxHeight={showMore || maxHeight}
                        overflow='hidden'
                        css={{
                            whiteSpace: 'normal',
                            ...HTML_COMP_STYLE
                        }}
                        dangerouslySetInnerHTML={{
                            __html: `<div>${description}</div>`
                        }}
                    />
                    {hasOverflow && (
                        <Link
                            onClick={toggleOverflow}
                            color='blue'
                            padding={2}
                            marginBottom={-2}
                            marginX={-2}
                            children={getText(showMore ? 'showLess' : 'showMore')}
                        />
                    )}
                </div>
            </Grid>
        </>
    );
};

const HTML_COMP_STYLE = {
    ...typography,
    '& > div :first-child': {
        marginTop: 0
    },
    '& > div :last-child': {
        marginBottom: 0
    }
};

// Preserve the class's shouldComponentUpdate allow-list — wrapUFEFunctional reads
// this property inside its React.memo comparator (shouldPreventRender). Without it
// the memo would fall back to a full shallow-prop compare and re-render on any
// prop change (e.g. product, dataAt, hideBreadcrumbs, isRegularProductSmallView),
// triggering the description-measurement effect chain more often than the class did.
Info.shouldUpdatePropsOn = ['currentSku.skuId', 'currentSku.actionFlags', 'title', 'description'];

export default wrapFunctionalComponent(Info, 'Info');
